<template>
    <div class="well p-4">
        <div class="flex-level flex-top">
            <div class="wp-5">
                <section class="mb-4">
                    <h3 class="mt-0 mb-2 lh-24 fs-4 bold">{{ $l10n.__('Informations') }}</h3>

                    <article class="mb-3">
                        <CompassFormItem
                            :label="$t.__('common.labels.currency')"
                            :error="errors.get('currency_id')"
                        >
                            <CompassSelect
                                v-model="form.currency"
                                class="input-half"
                                :class="{ 'select-error': errors.has('currency_id') }"
                            >
                                <CompassDropdownOption
                                    v-for="currency in currencies"
                                    :key="currency.id"
                                    :label="currency.symbol"
                                    :value="+currency.id"
                                    :disabled="currency.enabled !== 'Y'"
                                />
                            </CompassSelect>
                        </CompassFormItem>
                    </article>
                    <article class="mb-3">
                        <CompassFormItem
                            :label="$t.__('overview.tabs.actions.EditPreferencesThird.carrier')"
                            :error="errors.get('carrier_id')"
                        >
                            <CompassSelect
                                v-model="form.shipper"
                                class="input-half"
                                :class="{ 'select-error': errors.has('carrier_id') }"
                            >
                                <CompassDropdownOption
                                    v-for="shipper in metadata.shippers"
                                    :key="shipper.value"
                                    :label="shipper.label"
                                    :value="shipper.value"
                                />
                            </CompassSelect>
                        </CompassFormItem>
                    </article>
                    <article class="mb-3">
                        <CompassFormItem
                            :label="$t.__('overview.tabs.actions.EditPreferencesThird.default_VAT')"
                            :error="errors.get('tax_id')"
                        >
                            <CompassSelect
                                v-model="form.tax"
                                class="wp-10"
                                :class="{ 'select-error': errors.has('tax_id') }"
                            >
                                <CompassDropdownOption
                                    v-for="tax in taxes"
                                    :key="tax.id"
                                    :label="tax.label"
                                    :value="tax.id"
                                />
                            </CompassSelect>
                        </CompassFormItem>
                    </article>
                    <article class="mb-3">
                        <CompassFormItem
                            :label="
                                $t.__('overview.tabs.actions.EditPreferencesThird.payment_term')
                            "
                            :error="errors.get('paymentTerm.xdays')"
                        >
                            <CompassSelect
                                v-model="form.paymentTerm.id"
                                class="input-half"
                                :class="{ 'select-error': errors.has('payment_terms_id') }"
                                @change="clearEndOfMonth"
                            >
                                <CompassDropdownOption
                                    v-for="paymentTerm in metadata.paymentTerms"
                                    :key="paymentTerm.value"
                                    :label="paymentTerm.label"
                                    :value="paymentTerm.value"
                                />
                            </CompassSelect>
                            <CompassCheckbox
                                v-if="enableEndOfMonthBox"
                                :modelValue="
                                    $t.__('overview.tabs.actions.EditPreferencesThird.end_month')
                                "
                                :checked="form.paymentTerm.endOfMonth"
                                class="mt-3"
                                @change="checkEndOfMonth"
                            />
                            <CompassInput
                                v-if="enableXDays"
                                v-model="form.paymentTerm.xdays"
                                class="mt-3 input-half"
                                :error="errors.has('paymentTerm.xdays')"
                                :placeholder="
                                    $t.__(
                                        'overview.tabs.actions.EditPreferencesThird.enter_day_number',
                                    )
                                "
                            />
                        </CompassFormItem>
                    </article>
                    <article class="mb-3">
                        <CompassFormItem
                            :label="
                                $t.__('overview.tabs.actions.EditPreferencesThird.global_discount')
                            "
                        >
                            <div class="amount-group">
                                <InputWithSelect
                                    v-model="form.discount"
                                    :options="[
                                        { label: '€', value: 'amount' },
                                        { label: '%', value: 'percent' },
                                    ]"
                                />
                            </div>
                        </CompassFormItem>
                    </article>
                    <article>
                        <CompassFormItem
                            v-if="!!metadata.bankAccounts.length"
                            :label="
                                $t.__('overview.tabs.actions.EditPreferencesThird.bank_account')
                            "
                            :error="errors.get('bank_account_id')"
                        >
                            <CompassSelect
                                v-model="form.bankAccount"
                                :class="{ 'select-error': errors.has('bank_account_id') }"
                            >
                                <CompassDropdownOption
                                    v-for="account in metadata.bankAccounts"
                                    :key="account.value"
                                    :label="account.label"
                                    :value="account.value"
                                />
                            </CompassSelect>
                        </CompassFormItem>
                        <CompassCheckbox
                            :modelValue="
                                $t.__('overview.tabs.actions.EditPreferencesThird.each_line')
                            "
                            size="medium"
                            :checked="form.discountByRow"
                            class="mt-3"
                            @change="(val) => (form.discountByRow = val)"
                        />
                    </article>
                </section>

                <section v-if="!isSupplier && Object.keys(metadata.dunning).length">
                    <h3 class="mt-0 mb-2 lh-24 fs-4 bold">
                        {{ $l10n.__('Plan de relance') }}
                    </h3>
                    <article v-if="metadata.dunning.estimate" class="mb-3">
                        <CompassFormItem
                            :label="
                                $t.__('overview.tabs.actions.EditPreferencesThird.estimate_plan')
                            "
                            error=""
                        >
                            <CompassSelect v-model="form.dunning.estimate.value">
                                <CompassDropdownOption
                                    v-for="planEstimate in metadata.dunning.estimate"
                                    :key="planEstimate.id"
                                    :label="planEstimate.label"
                                    :value="planEstimate.value"
                                />
                            </CompassSelect>
                        </CompassFormItem>
                    </article>
                    <article v-if="metadata.dunning.invoice" class="mb-3">
                        <CompassFormItem
                            :label="
                                $t.__('overview.tabs.actions.EditPreferencesThird.invoice_plan')
                            "
                            error=""
                        >
                            <CompassSelect v-model="form.dunning.invoice.value">
                                <CompassDropdownOption
                                    v-for="planEstimate in metadata.dunning.invoice"
                                    :key="planEstimate.id"
                                    :label="planEstimate.label"
                                    :value="planEstimate.value"
                                />
                            </CompassSelect>
                        </CompassFormItem>
                    </article>
                    <article v-if="metadata.dunning.proforma">
                        <CompassFormItem
                            :label="
                                $t.__('overview.tabs.actions.EditPreferencesThird.proforma_plan')
                            "
                            error=""
                        >
                            <CompassSelect v-model="form.dunning.proforma.value">
                                <CompassDropdownOption
                                    v-for="planEstimate in metadata.dunning.proforma"
                                    :key="planEstimate.id"
                                    :label="planEstimate.label"
                                    :value="planEstimate.value"
                                />
                            </CompassSelect>
                        </CompassFormItem>
                    </article>
                </section>
            </div>
            <div class="wp-5">
                <section>
                    <article v-if="!isSupplier" class="mb-3">
                        <CompassFormItem
                            :label="
                                $t.__('overview.tabs.actions.EditPreferencesThird.price_category')
                            "
                            :error="errors.get('rateCategory')"
                        >
                            <CompassSelect
                                v-model="form.rateCategory"
                                :class="{
                                    'select-error':
                                        errors.has('rateCategory') ||
                                        errors.has('rate_category_id'),
                                }"
                            >
                                <CompassDropdownOption
                                    v-for="rateCategory in metadata.rateCategories"
                                    :key="rateCategory.value"
                                    :label="rateCategory.label"
                                    :value="rateCategory.value"
                                />
                            </CompassSelect>
                        </CompassFormItem>
                    </article>
                    <article>
                        <CompassCheckboxGroup
                            :label="
                                $t.__('overview.tabs.actions.EditPreferencesThird.payment_method')
                            "
                        >
                            <CompassCheckbox
                                v-for="medium in metadata.payMediums"
                                :key="medium.value"
                                :modelValue="medium.label"
                                :checked="mediumState(medium.value)"
                                @change="(e) => setMedium(e, medium.value)"
                            />
                        </CompassCheckboxGroup>
                    </article>
                </section>
            </div>
        </div>
        <div class="flex-level flex-end mt-4">
            <CompassButton
                :label="$t.__('common.actions.cancel')"
                type="secondary"
                @click="close"
            ></CompassButton>
            <CompassButton
                :label="$t.__('common.actions.save')"
                :loading="loading"
                type="primary"
                @click="save"
            ></CompassButton>
        </div>
    </div>
</template>

<script>
import InputWithSelect from 'vcomponents/inputs/InputWithSelect.vue';
import { updatePreferences } from 'api/third';
import { createNamespacedHelpers } from 'vuex';
import { systemPaydatesWithEndOfMonth } from 'vcomponents/overview/tabs/mapping/ThirdMapping';
import Schema from 'async-validator';
import Errors from 'Core/commons/Errors';
import { dunningTypes } from 'api/third/thirddunnings.js';

const system = createNamespacedHelpers('system');
export default {
    components: {
        InputWithSelect,
    },
    props: {
        metadata: {
            type: Object,
            required: true,
        },
        preferences: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            form: {
                currency: this.preferences.currency.id || '',
                shipper: this.preferences.shipping.id || '',
                tax: this.preferences.tax.id || '',
                paymentTerm: {
                    id: this.preferences.paymentTerm.id,
                    endOfMonth: this.preferences.paymentTerm.endOfMonth,
                    xdays: this.preferences.paymentTerm.xDays,
                    code: null,
                },
                discount: {
                    input: this.preferences.discount.amount,
                    select: this.preferences.discount.isAmountDiscount ? 'amount' : 'percent',
                },
                bankAccount: +this.preferences.bankAccount.id || '',
                discountByRow: this.preferences.discountByRow,
                rateCategory: +this.preferences.rateCategory.id,
                payMediums: this.preferences.payMediums.map((m) => +m.id),
                dunning: {},
            },
            rules: {
                rateCategory: {
                    type: 'number',
                    message: this.$l10n.__('Veuillez renseigner une catégorie tarifaire'),
                    validator: (rule, value, callback) => {
                        if (!this.isSupplier && !value) return false;

                        return callback();
                    },
                },
                paymentTerm: {
                    type: 'object',
                    fields: {
                        xdays: {
                            type: 'number',
                            required: false,
                            message: this.$l10n.__('Veuillez renseigner un nombre de jours'),
                            validator: (rule, value, callback) => {
                                if (this.form.paymentTerm.code === 'xdays') {
                                    return value !== '' ? callback() : false;
                                }
                                return true;
                            },
                        },
                    },
                },
            },
            errors: new Errors(),
        };
    },
    computed: {
        ...system.mapState(['currencies']),
        isSupplier() {
            return this.$store.getters.identity.type === 'supplier';
        },
        thirdType() {
            return this.$store.getters['isCorporation'] ? 'companies' : 'individuals';
        },
        currentPaymentTerm() {
            const paymentTerm = this.metadata.paymentTerms.find(
                (term) => term.value === this.form.paymentTerm.id,
            );

            return _.isNil(paymentTerm) ? [] : paymentTerm;
        },
        enableEndOfMonthBox() {
            return (
                systemPaydatesWithEndOfMonth.includes(this.currentPaymentTerm?.code) ||
                (this.metadata.paymentTerms
                    .filter((term) => term.end_of_month === 'Y')
                    .map((term) => term.code)
                    .includes(this.currentPaymentTerm?.code) &&
                    this.currentPaymentTerm?.end_of_month === 'Y')
            );
        },
        enableXDays() {
            return this.currentPaymentTerm.code === 'xdays';
        },
        taxes() {
            return this.metadata.taxes
                .filter((tax) => !tax.is_ecotax && tax.is_active)
                .map((tax) => ({
                    ...tax,
                    label: tax.label ? `${tax.label} - ${tax.rate} %` : `${tax.rate} %`,
                }));
        },
    },
    created() {
        // Prevent to edit only activated dunnings
        dunningTypes.forEach((dunning) => {
            if (this.metadata.dunning[dunning]) {
                const formItem = this.preferences.dunning[dunning]
                    ? { ...this.preferences.dunning[dunning] }
                    : this.metadata.dunning[dunning].find((item) => item.value === 'main');
                this.form.dunning[dunning] = formItem;
            }
        });

        // Prevent to third have endOfMonth checked but non available for this paymentTerm
        this.clearEndOfMonth(this.preferences.paymentTerm.id);
    },
    methods: {
        close() {
            this.$emit('close');
        },
        save() {
            this.loading = true;

            const validator = new Schema(this.rules);

            validator
                .validate(this.form)
                .then(() => {
                    updatePreferences(
                        this.$store.getters.linkedid,
                        this.thirdType,
                        this.isSupplier,
                        this.form,
                    )
                        .then(() => {
                            this.$emit('saved');
                        })
                        .catch((error) => {
                            const parsedMessage = JSON.parse(error.message);
                            const errorsDetails = parsedMessage.data.error.details;

                            Object.keys(errorsDetails).forEach((fieldName) => {
                                this.errors.setOneError(fieldName, errorsDetails[fieldName]);
                            });
                        })
                        .finally(() => {
                            this.loading = false;
                        });
                })
                .catch(({ errors }) => {
                    errors.forEach((error) => {
                        this.errors.setOneError(error.field, error.message);
                    });
                    this.loading = false;
                });
        },
        mediumState(id) {
            return !!this.form.payMediums.find((medium) => medium === id);
        },
        setMedium(value, id) {
            if (value) {
                this.form.payMediums.push(id);
            } else {
                const index = this.form.payMediums.findIndex((medium) => medium === id);
                this.form.payMediums.splice(index, 1);
            }
        },
        clearEndOfMonth(value) {
            this.form.paymentTerm.id = value;
            this.form.paymentTerm.code = this.currentPaymentTerm.code;
            if (!this.enableEndOfMonthBox) {
                delete this.form.paymentTerm.endOfMonth;
            } else {
                this.form.paymentTerm.endOfMonth = this.form.paymentTerm.endOfMonth ?? false;
            }
            if (!this.enableXDays) {
                this.errors.clear('paymentTerm.xdays');
                this.form.paymentTerm.xdays = '';
            }
        },
        checkEndOfMonth(value) {
            this.form.paymentTerm.endOfMonth = value;
        },
    },
};
</script>

<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;

.well > .flex-level {
    gap: 16px;
}

section {
    &:last-child {
        margin-top: 2px;
    }
}

.input-half {
    width: 50%;
    max-width: 220px;
}

.select-error {
    &:deep(.compass-input) {
        border-color: $alert-50;
    }
}

.amount-group {
    &::v-deep {
        .input-with-select {
            .el-input-group__append {
                background-color: $white;
                color: $gray-100;
                padding: 0 2px 0 0;
            }

            .el-input {
                &.is-focus {
                    .el-input__inner {
                        border-color: transparent !important;
                    }
                }
            }

            .el-select__caret {
                font-size: 17px;
                font-weight: $font-weight-semibold;

                &.is-reverse {
                    transform: rotate(180deg);
                }
            }
        }
    }
}
</style>
